/* import __COLOCATED_TEMPLATE__ from './list-item.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import Component from '@glimmer/component';
import { ReviewType } from 'embercom/models/content-service/content-review-request';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { EntityType } from 'embercom/models/data/entity-types';
import { objectNames } from 'embercom/models/data/matching-system/matching-constants';
import type KnowledgeHubEditorService from 'embercom/services/knowledge-hub-editor-service';
import { action } from '@ember/object';
import { EditorMode } from 'embercom/components/knowledge-hub/content-editor/drawer-editor';
import { tracked } from '@glimmer/tracking';
import type AiContentLibraryApi from 'embercom/lib/ai-content-library/list-api';
interface Args {
  contentWrapper: ContentWrapper;
  activeContentId?: number;
  activeContentType?: string;
  listApi?: AiContentLibraryApi;
}

export default class ListItem extends Component<Args> {
  @service declare intl: IntlService;
  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;

  @tracked isHovering = false;

  get content() {
    return this.args.contentWrapper.contents.firstObject;
  }

  get isActive() {
    return (
      Number(this.args.activeContentId) === this.contentId &&
      this.args.activeContentType === this.contentObjectName
    );
  }

  get contentId() {
    return this.content?.contentId;
  }

  get contentType() {
    return this.content?.contentType;
  }

  get contentObjectName() {
    return objectNames[this.contentType!];
  }

  get reviewReason() {
    return this.content?.contentData.latest_content_review_request_reason;
  }

  get topic() {
    return this.content?.contentData?.suggestion_metadata?.topic;
  }

  get mostRecentContentReviewRequestCreatedAt() {
    return this.content?.contentData.most_recent_content_review_request_created_at;
  }

  get leastRecentContentReviewRequestCreatedAt() {
    return this.content?.contentData.least_recent_content_review_request_created_at;
  }

  get suggestedAt() {
    return this.args.listApi?.searchParams?.sort_direction === 'asc'
      ? this.leastRecentContentReviewRequestCreatedAt
      : this.mostRecentContentReviewRequestCreatedAt;
  }

  get icon() {
    if (this.contentType === EntityType.ContentSnippet) {
      return 'note';
    }

    if (this.contentType === EntityType.Article) {
      return 'article';
    }

    return 'article';
  }

  get title() {
    let reviewType = this.content?.contentReviewType;
    let contentTitle = this.args.contentWrapper.title;
    let objectKey = this.contentType === EntityType.ContentSnippet ? 'snippet' : 'article';

    if (reviewType === ReviewType.CREATE_CONTENT) {
      return `${this.intl.t(`ai-agent.suggestions.list-item.titles.add-${objectKey}`)}: ${contentTitle}`;
    } else if (reviewType === ReviewType.EDIT_CONTENT) {
      return `${this.intl.t(`ai-agent.suggestions.list-item.titles.edit-${objectKey}`)}: ${contentTitle}`;
    } else if (reviewType === ReviewType.DELETE_CONTENT) {
      return `${this.intl.t(`ai-agent.suggestions.list-item.titles.delete-${objectKey}`)}: ${contentTitle}`;
    }
    return this.intl.t(`ai-agent.suggestions.list-item.titles.fallback-${objectKey}`);
  }

  get conversationsCount() {
    return (
      this.args.contentWrapper.contentReviewSuggestionMetadata.included_conversations?.length || 0
    );
  }

  @action
  openSuggestionInEditor() {
    if (!this.contentId || !this.contentType) {
      return;
    }

    this.knowledgeHubEditorService.openActiveContent(
      this.contentId,
      this.contentObjectName,
      EditorMode.REVIEW,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Suggestions::ListItem': typeof ListItem;
    'ai-agent/suggestions/list-item': typeof ListItem;
  }
}
